// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Observation, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { RPM_OBSERVATION_CODES, RPM_OBSERVATION_SYSTEM } from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

import { buildRpmHistoryRows, type RpmHistoryMonthRow } from '../utils/rpmNotes';

const RPM_HISTORY_CODE_FILTER = Object.values(RPM_OBSERVATION_CODES)
  .map((code) => `${RPM_OBSERVATION_SYSTEM}|${code}`)
  .join(',');

export interface UsePatientRpmHistoryResult {
  rows: RpmHistoryMonthRow[];
  loading: boolean;
  refresh: () => void;
}

export function usePatientRpmHistory(patient: Patient | undefined): UsePatientRpmHistoryResult {
  const medplum = useMedplum();
  const [rows, setRows] = useState<RpmHistoryMonthRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const observations = (await medplum.searchResources('Observation', {
        subject: `Patient/${patient.id}`,
        code: RPM_HISTORY_CODE_FILTER,
        _count: '250',
        _sort: '-date',
      })) as Observation[];
      setRows(buildRpmHistoryRows(observations));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { rows, loading, refresh: fetch };
}
